import React, { useState } from 'react';
import { 
  ChevronLeft, Plus, Trash2, GripVertical, Save, Search,
  DollarSign, HelpCircle, ArrowRight, FileText, Sparkles, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';

// AI Generate Button Component
const AIGenerateButton = ({ section, label, generating, onGenerate }) => (
  <Button
    onClick={() => onGenerate(section)}
    disabled={generating !== null}
    variant="ghost"
    size="sm"
    className="text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 text-xs gap-1.5"
    data-testid={`ai-generate-${section}`}
  >
    {generating === section ? (
      <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
    ) : (
      <><Sparkles className="w-3 h-3" /> {label}</>
    )}
  </Button>
);

const ServiceSEOEditor = ({ service, onBack, onSaved }) => {
  const [activeTab, setActiveTab] = useState('meta');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null); // tracks which section is generating
  const [form, setForm] = useState({
    title: service.title || '',
    slug: service.slug || '',
    short_description: service.short_description || '',
    full_description: service.full_description || '',
    image_url: service.image_url || '',
    features: service.features || [],
    meta_title: service.meta_title || '',
    meta_description: service.meta_description || '',
    pricing_factors: service.pricing_factors || [],
    process_steps: service.process_steps || [],
    faqs: service.faqs || [],
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.admin.updateService(service.id, form);
      toast.success('Service updated');
      onSaved();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // --- Array helpers ---
  const addPricingFactor = () => {
    setForm({ ...form, pricing_factors: [...form.pricing_factors, { title: '', description: '' }] });
  };
  const updatePricingFactor = (index, field, value) => {
    const updated = [...form.pricing_factors];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, pricing_factors: updated });
  };
  const removePricingFactor = (index) => {
    setForm({ ...form, pricing_factors: form.pricing_factors.filter((_, i) => i !== index) });
  };

  const addProcessStep = () => {
    const nextStep = form.process_steps.length + 1;
    setForm({ ...form, process_steps: [...form.process_steps, { step: nextStep, title: '', description: '' }] });
  };
  const updateProcessStep = (index, field, value) => {
    const updated = [...form.process_steps];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, process_steps: updated });
  };
  const removeProcessStep = (index) => {
    const updated = form.process_steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step: i + 1 }));
    setForm({ ...form, process_steps: updated });
  };

  const addFaq = () => {
    setForm({ ...form, faqs: [...form.faqs, { question: '', answer: '' }] });
  };
  const updateFaq = (index, field, value) => {
    const updated = [...form.faqs];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, faqs: updated });
  };
  const removeFaq = (index) => {
    setForm({ ...form, faqs: form.faqs.filter((_, i) => i !== index) });
  };

  const addFeature = () => {
    setForm({ ...form, features: [...form.features, ''] });
  };
  const updateFeature = (index, value) => {
    const updated = [...form.features];
    updated[index] = value;
    setForm({ ...form, features: updated });
  };
  const removeFeature = (index) => {
    setForm({ ...form, features: form.features.filter((_, i) => i !== index) });
  };

  // --- AI Generate ---
  const handleAIGenerate = async (section) => {
    // Confirm if replacing existing content
    const hasExisting = (section === 'steps' || section === 'all') && form.process_steps.length > 0
      || (section === 'pricing' || section === 'all') && form.pricing_factors.length > 0
      || (section === 'faqs' || section === 'all') && form.faqs.length > 0;
    if (hasExisting && !window.confirm('This will replace the existing content in this section. Continue?')) return;

    setGenerating(section);
    try {
      const res = await api.admin.aiGenerateServiceSEO(form.title, form.short_description || form.full_description, section);
      const data = res.data;

      const updates = { ...form };
      if (data.meta_title !== undefined) updates.meta_title = data.meta_title;
      if (data.meta_description !== undefined) updates.meta_description = data.meta_description;
      if (data.process_steps) updates.process_steps = data.process_steps;
      if (data.pricing_factors) updates.pricing_factors = data.pricing_factors;
      if (data.faqs) updates.faqs = data.faqs;

      setForm(updates);
      toast.success(section === 'all' ? 'All SEO content generated!' : `${section} content generated!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI generation failed');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="seo-editor">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-white/40 hover:text-white transition-colors"
            data-testid="seo-editor-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-white font-bold text-lg">{form.title}</h2>
            <p className="text-white/40 text-xs">/{form.slug}</p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-electric-teal text-primary-dark font-bold"
          data-testid="seo-editor-save"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {/* Generate All Banner */}
      {(form.process_steps.length === 0 || form.pricing_factors.length === 0 || form.faqs.length === 0) && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-amber-400 font-medium text-sm">Missing SEO content?</p>
            <p className="text-white/40 text-xs mt-0.5">AI can generate all sections at once — process steps, pricing factors, FAQs, and meta tags.</p>
          </div>
          <Button
            onClick={() => handleAIGenerate('all')}
            disabled={generating !== null}
            className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 font-bold text-xs flex-shrink-0"
            data-testid="ai-generate-all"
          >
            {generating === 'all' ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating All...</>
            ) : (
              <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate All with AI</>
            )}
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-primary-dark border border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="meta" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark text-xs">
            <Search className="w-3 h-3 mr-1" /> SEO Meta
          </TabsTrigger>
          <TabsTrigger value="basic" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark text-xs">
            <FileText className="w-3 h-3 mr-1" /> Basic Info
          </TabsTrigger>
          <TabsTrigger value="steps" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark text-xs">
            <ArrowRight className="w-3 h-3 mr-1" /> Process Steps
          </TabsTrigger>
          <TabsTrigger value="pricing" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark text-xs">
            <DollarSign className="w-3 h-3 mr-1" /> Pricing Factors
          </TabsTrigger>
          <TabsTrigger value="faqs" className="data-[state=active]:bg-electric-teal data-[state=active]:text-primary-dark text-xs">
            <HelpCircle className="w-3 h-3 mr-1" /> FAQs
          </TabsTrigger>
        </TabsList>

        {/* SEO Meta Tab */}
        <TabsContent value="meta">
          <div className="bg-secondary-dark rounded-lg border border-white/10 p-5 space-y-5">
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-electric-teal text-xs font-bold uppercase tracking-wider">Search Engine Preview</p>
                <AIGenerateButton section="meta" label="AI Generate Meta" generating={generating} onGenerate={handleAIGenerate} />
              </div>
              <div className="bg-white rounded-lg p-4 mb-5">
                <p className="text-blue-700 text-lg font-medium leading-snug truncate">
                  {form.meta_title || form.title + ' | Island Coastal Electric'}
                </p>
                <p className="text-green-700 text-sm mt-0.5">
                  islandcoastalelectric.ca/services/{form.slug}
                </p>
                <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                  {form.meta_description || form.short_description}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-white/60">Meta Title <span className={`${(form.meta_title || '').length > 60 ? 'text-red-400' : 'text-white/30'}`}>({(form.meta_title || '').length}/60 chars)</span></Label>
              <Input
                value={form.meta_title}
                onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                placeholder="e.g., EV Charger Installation Victoria BC | Island Coastal Electric"
                data-testid="meta-title-input"
              />
              <p className="text-white/30 text-xs mt-1">What shows as the blue link in Google. Keep under 60 characters.</p>
            </div>
            <div>
              <Label className="text-white/60">Meta Description <span className={`${(form.meta_description || '').length > 160 ? 'text-red-400' : 'text-white/30'}`}>({(form.meta_description || '').length}/160 chars)</span></Label>
              <Textarea
                value={form.meta_description}
                onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                className="bg-primary-dark border-white/20 text-white mt-1"
                rows={3}
                placeholder="e.g., Professional EV charger installation across Vancouver Island..."
                data-testid="meta-desc-input"
              />
              <p className="text-white/30 text-xs mt-1">What shows below the link in Google. Keep under 160 characters.</p>
            </div>
          </div>
        </TabsContent>

        {/* Basic Info Tab */}
        <TabsContent value="basic">
          <div className="bg-secondary-dark rounded-lg border border-white/10 p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white/60">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-primary-dark border-white/20 text-white mt-1" data-testid="basic-title-input" />
              </div>
              <div>
                <Label className="text-white/60">Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-primary-dark border-white/20 text-white mt-1" data-testid="basic-slug-input" />
              </div>
            </div>
            <div>
              <Label className="text-white/60">Short Description</Label>
              <Textarea value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="bg-primary-dark border-white/20 text-white mt-1" rows={2} />
            </div>
            <div>
              <Label className="text-white/60">Full Description</Label>
              <Textarea value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} className="bg-primary-dark border-white/20 text-white mt-1" rows={5} />
            </div>
            <div>
              <Label className="text-white/60">Image URL</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-primary-dark border-white/20 text-white mt-1" />
            </div>

            {/* Features list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white/60">Features (What&apos;s Included)</Label>
                <Button onClick={addFeature} variant="ghost" size="sm" className="text-electric-teal text-xs" data-testid="add-feature-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {form.features.map((feature, index) => (
                  <div key={`feat-${index}-${feature.slice(0, 10)}`} className="flex gap-2">
                    <Input
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className="bg-primary-dark border-white/20 text-white text-sm"
                      placeholder="e.g., Level 2 Charger Installation"
                    />
                    <Button onClick={() => removeFeature(index)} variant="ghost" size="sm" className="text-red-500/60 hover:text-red-400 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Process Steps Tab */}
        <TabsContent value="steps">
          <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-medium text-sm">How It Works — Process Steps</p>
                <p className="text-white/30 text-xs mt-0.5">These appear as numbered steps on the service page.</p>
              </div>
              <div className="flex gap-1">
                <AIGenerateButton section="steps" label="AI Generate" generating={generating} onGenerate={handleAIGenerate} />
                <Button onClick={addProcessStep} variant="ghost" size="sm" className="text-electric-teal text-xs" data-testid="add-step-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Step
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {form.process_steps.map((step, index) => (
                <div key={`step-${step.step}-${index}`} className="bg-primary-dark rounded-lg p-4 border border-white/5" data-testid={`step-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-electric-teal/10 border border-electric-teal/30 flex items-center justify-center text-electric-teal font-bold text-xs">
                        {step.step}
                      </span>
                      <span className="text-white/40 text-xs">Step {step.step}</span>
                    </div>
                    <Button onClick={() => removeProcessStep(index)} variant="ghost" size="sm" className="text-red-500/60 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={step.title}
                      onChange={(e) => updateProcessStep(index, 'title', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      placeholder="Step title, e.g., Free Site Assessment"
                    />
                    <Textarea
                      value={step.description}
                      onChange={(e) => updateProcessStep(index, 'description', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      rows={2}
                      placeholder="What happens in this step..."
                    />
                  </div>
                </div>
              ))}
              {form.process_steps.length === 0 && (
                <p className="text-white/20 text-sm text-center py-6">No process steps yet. Click &quot;Add Step&quot; to create one.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Pricing Factors Tab */}
        <TabsContent value="pricing">
          <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-medium text-sm">What Affects the Cost — Pricing Factors</p>
                <p className="text-white/30 text-xs mt-0.5">Explain what drives the price. Builds trust and sets expectations.</p>
              </div>
              <div className="flex gap-1">
                <AIGenerateButton section="pricing" label="AI Generate" generating={generating} onGenerate={handleAIGenerate} />
                <Button onClick={addPricingFactor} variant="ghost" size="sm" className="text-electric-teal text-xs" data-testid="add-pricing-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Factor
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {form.pricing_factors.map((factor, index) => (
                <div key={`pf-${index}-${(factor.title || '').slice(0, 10)}`} className="bg-primary-dark rounded-lg p-4 border border-white/5" data-testid={`pricing-${index}`}>
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-4 h-4 text-electric-teal/50" />
                    <Button onClick={() => removePricingFactor(index)} variant="ghost" size="sm" className="text-red-500/60 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={factor.title}
                      onChange={(e) => updatePricingFactor(index, 'title', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      placeholder="Factor name, e.g., Charger Type"
                    />
                    <Textarea
                      value={factor.description}
                      onChange={(e) => updatePricingFactor(index, 'description', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      rows={2}
                      placeholder="How this factor affects the price..."
                    />
                  </div>
                </div>
              ))}
              {form.pricing_factors.length === 0 && (
                <p className="text-white/20 text-sm text-center py-6">No pricing factors yet. Click &quot;Add Factor&quot; to create one.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs">
          <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-medium text-sm">Frequently Asked Questions</p>
                <p className="text-white/30 text-xs mt-0.5">These appear as an accordion on the service page + FAQ schema for Google rich snippets.</p>
              </div>
              <div className="flex gap-1">
                <AIGenerateButton section="faqs" label="AI Generate" generating={generating} onGenerate={handleAIGenerate} />
                <Button onClick={addFaq} variant="ghost" size="sm" className="text-electric-teal text-xs" data-testid="add-faq-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add FAQ
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {form.faqs.map((faq, index) => (
                <div key={`faq-${index}-${(faq.question || '').slice(0, 10)}`} className="bg-primary-dark rounded-lg p-4 border border-white/5" data-testid={`faq-editor-${index}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/30 text-xs">Q{index + 1}</span>
                    <Button onClick={() => removeFaq(index)} variant="ghost" size="sm" className="text-red-500/60 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Input
                      value={faq.question}
                      onChange={(e) => updateFaq(index, 'question', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      placeholder="Question, e.g., How long does installation take?"
                    />
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                      className="bg-secondary-dark border-white/10 text-white text-sm"
                      rows={3}
                      placeholder="Answer — be specific, helpful, and local. This shows on your site AND in Google search results."
                    />
                  </div>
                </div>
              ))}
              {form.faqs.length === 0 && (
                <p className="text-white/20 text-sm text-center py-6">No FAQs yet. Click &quot;Add FAQ&quot; to create one.</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Floating save bar */}
      <div className="sticky bottom-0 bg-secondary-dark/95 backdrop-blur border-t border-white/10 -mx-4 md:-mx-6 px-4 md:px-6 py-3 flex items-center justify-between">
        <p className="text-white/30 text-xs">
          {form.process_steps.length} steps &middot; {form.pricing_factors.length} pricing factors &middot; {form.faqs.length} FAQs
        </p>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-electric-teal text-primary-dark font-bold"
          data-testid="seo-editor-save-bottom"
        >
          <Save className="w-4 h-4 mr-1" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>
    </div>
  );
};

export default ServiceSEOEditor;
